import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OpenTab } from '../context/OpenTabsContext';
import { setShortcutDragPayload } from '../utils/shortcutDnd';

interface OpenTabItemProps {
  tab: OpenTab;
  isActive: boolean;
  onNavigate: (tab: OpenTab) => void;
  onClose: (tab: OpenTab) => void;
}

const OpenTabItem: React.FC<OpenTabItemProps> = ({ tab, isActive, onNavigate, onClose }) => {
  const { t } = useTranslation();

  // Exact same className strings as ShortcutTab.tsx
  const tabBase =
    'relative flex items-center gap-1.5 px-3 rounded-t-lg border-t border-l border-r border-b-0 ' +
    'transition-all duration-150 cursor-grab active:cursor-grabbing whitespace-nowrap flex-shrink-0 ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-500 select-none';

  const activeClass = 'pt-2 pb-2 -mb-px text-white shadow-md z-10';

  const inactiveClass =
    'pt-1.5 pb-2 bg-gray-100 text-gray-600 border-gray-300 ' +
    'hover:bg-gray-50 hover:text-gray-800';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onNavigate(tab);
    }
  };

  // Drag onto ShortcutNavBar (Кратенки) — same MIME as TopNavLink menu entries.
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    setShortcutDragPayload(e.dataTransfer, {
      path: tab.path,
      label: tab.label,
      state: tab.state,
    });
    e.dataTransfer.setData('text/plain', tab.label);
  };

  return (
    <div
      role="tab"
      tabIndex={0}
      aria-selected={isActive}
      draggable
      onDragStart={handleDragStart}
      onClick={() => onNavigate(tab)}
      onKeyDown={handleKeyDown}
      className={`${tabBase} ${isActive ? activeClass : inactiveClass}`}
      style={
        isActive
          ? {
              background: 'linear-gradient(to right, var(--color-primary), var(--color-secondary))',
              borderColor: 'var(--color-primary)',
            }
          : undefined
      }
      title={tab.label}
    >
      {/* Tab label — same markup as ShortcutTab */}
      <span className="font-semibold text-sm leading-none">{tab.label}</span>

      {/* Close button — same styling as ShortcutTab's remove button */}
      <button
        draggable={false}
        onClick={(e) => {
          e.stopPropagation();
          onClose(tab);
        }}
        className={`p-0.5 rounded transition-colors cursor-pointer ${
          isActive
            ? 'text-white/70 hover:text-white hover:bg-white/20'
            : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
        }`}
        aria-label={t('tabs.closeTab')}
        title={t('tabs.closeTab') || 'Close tab'}
      >
        <X size={13} />
      </button>
    </div>
  );
};

export default OpenTabItem;
